/**
 * @file 学员列表筛选项
 * @description RequestTable searchForm 表单项；配合展开 / 收起。
 *
 * @whenToUse 仅学员列表 RequestTable。
 * @whenNotToUse 其他业务筛选。
 */

import { DatePicker } from '@/components/ui/date-picker.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { GradeSelect } from '@/components/ui/grade-select.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { Select } from '@/components/ui/select.tsx'
import type { RequestTableSearchContext } from '@/components/ui/request-table.tsx'
import {
  LEARNING_LEVEL_OPTIONS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  TEXTBOOK_OPTIONS,
  type StudentQuery,
} from '@/components/pages/student-page/mock.ts'

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(STUDENT_STATUS_LABEL).map(([value, label]) => ({
    value,
    label,
  })),
]

const ALL_OPTION = { value: '', label: '全部' } as const

/**
 * 学员列表筛选项（基础三项常显，其余随 expanded 展开）
 * @param ctx RequestTable 搜索上下文
 */
export function StudentSearchFields(
  ctx: RequestTableSearchContext<StudentQuery>,
) {
  const { values, setField, expanded } = ctx

  return (
    <>
      <FormField label="学员名称" className="w-44">
        <Input
          value={values.name}
          placeholder="姓名关键字"
          onChange={(event) => setField('name', event.target.value)}
        />
      </FormField>
      <FormField label="状态" className="w-36">
        <Select
          value={values.status}
          placeholder="全部"
          options={STATUS_OPTIONS}
          onChange={(value) => setField('status', value)}
        />
      </FormField>
      <FormField label="创建时间" className="w-44">
        <DatePicker
          value={values.createdAt || undefined}
          placeholder="选择日期"
          onChange={(value) => setField('createdAt', value)}
        />
      </FormField>
      {expanded ? (
        <>
          <FormField label="科目" className="w-36">
            <Select
              value={values.subject}
              placeholder="全部"
              options={[ALL_OPTION, ...SUBJECT_OPTIONS]}
              onChange={(value) => setField('subject', value)}
            />
          </FormField>
          <FormField label="教材" className="w-44">
            <Select
              value={values.textbook}
              placeholder="全部"
              options={[ALL_OPTION, ...TEXTBOOK_OPTIONS]}
              onChange={(value) => setField('textbook', value)}
            />
          </FormField>
          <FormField label="阶段" className="w-36">
            <Select
              value={values.stage}
              placeholder="全部"
              options={[ALL_OPTION, ...STAGE_OPTIONS]}
              onChange={(value) => setField('stage', value)}
            />
          </FormField>
          <FormField label="年级" className="w-36">
            <GradeSelect
              value={values.grade}
              stage={values.stage}
              allowEmpty
              placeholder="全部"
              onChange={(value) => setField('grade', value)}
            />
          </FormField>
          <FormField label="学期" className="w-36">
            <Select
              value={values.semester}
              placeholder="全部"
              options={[ALL_OPTION, ...SEMESTER_OPTIONS]}
              onChange={(value) => setField('semester', value)}
            />
          </FormField>
          <FormField label="学习力等级" className="w-36">
            <Select
              value={values.learningLevel}
              placeholder="全部"
              options={[ALL_OPTION, ...LEARNING_LEVEL_OPTIONS]}
              onChange={(value) => setField('learningLevel', value)}
            />
          </FormField>
          <FormField label="学习力" className="w-36">
            <InputNumber
              value={values.learningPower}
              min={0}
              placeholder="不限"
              onChange={(value) => setField('learningPower', value)}
            />
          </FormField>
        </>
      ) : null}
    </>
  )
}
